import type { ReactElement } from 'react';
import { useState, useEffect } from 'react';
import { Button, Modal } from '@hotjar/hotsauce';

import type { GlobalModalProps } from '@hotjar/webapp/features/GlobalModal/createGlobalModal';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { detachPrefix } from '@hotjar/webapp/billing/tax';
import { isZuoraEnabled } from '@hotjar/webapp/domains/billing/features';
import { mapOwnedAccountModelToDto, useOwnedAccount } from '@hotjar/account';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';

import type { FormInputs } from './CompanyDetailsForm/CompanyDetailsForm';
import { CompanyDetailsForm } from './CompanyDetailsForm/CompanyDetailsForm';
import type { FormInputs as LegacyFormInputs } from '../legacy/CompanyDetails/CompanyDetailsForm/LegacyCompanyDetailsForm';
import { LegacyCompanyDetailsForm } from '../legacy/CompanyDetails/CompanyDetailsForm/LegacyCompanyDetailsForm';
import { MODAL_NAME } from '../legacy/CompanyDetails/constants';

const FORM_ID = 'company-details';

export const CompanyDetailsModal = ({
  onCancel,
  onSubmit,
}: GlobalModalProps<FormInputs>): ReactElement | null => {
  const { t: tCommon } = useTranslationWithPrefix('common:actions');
  const { t: tBilling } = useTranslationWithPrefix('billing:company-details');
  const [isLoading, setIsLoading] = useState(false);

  const { data: account } = useOwnedAccount();
  const { loadedModal, closedModal, getGroupDataForAccount } = useTrack();

  const zuoraEnabled = isZuoraEnabled(account);
  const countryCode = account?.location?.split(',')[1] as LegacyFormInputs['countryCode'];

  const vatNumber = detachPrefix(account?.vatNumber, countryCode);

  const title = tBilling(zuoraEnabled ? 'title' : 'title-legacy');

  const handleModalClose = () => {
    closedModal(MODAL_NAME, account ? getGroupDataForAccount(account) : undefined);
    onCancel();
  };

  useEffect(() => {
    loadedModal(MODAL_NAME, account ? getGroupDataForAccount(account) : undefined);
  }, [loadedModal, account, getGroupDataForAccount]);

  if (!account) return null;
  return (
    <Modal contentLabel={title} isOpen onClose={handleModalClose}>
      <Modal.Header title={title} subtitle={!zuoraEnabled && tBilling('description')} />
      <Modal.Body>
        {zuoraEnabled && <CompanyDetailsForm onSubmit={onSubmit} formId={FORM_ID} />}
        {!zuoraEnabled && (
          <LegacyCompanyDetailsForm
            onSuccess={onSubmit}
            onLoadingChange={setIsLoading}
            formId={FORM_ID}
            defaultValues={{
              company: account.company ?? '',
              address: account.address ?? undefined,
              countryCode,
              vatNumber,
            }}
            account={mapOwnedAccountModelToDto(account)}
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={handleModalClose} kind="ghost">
          {tCommon('cancel')}
        </Button>
        <Button
          isLoading={isLoading}
          loadingText={tBilling('submit-loading')}
          type="submit"
          form={FORM_ID}
        >
          {tCommon('continue')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
